export const M4_UNIT_17 = {
  id: "m4-u17-geography",
  title: "Unidad 17: Geographical Features / Características Geográficas",
  topic: "Explore the world's geography! Learn vocabulary to describe nature - mountains, rivers, oceans, deserts, and more. Essential for travel, describing places, and understanding the world around you!",
  iconType: "globe",
  grammarImage: "/grammar/grammar-m4-u17.jpg",
  grammarText: "VOCABULARY FOCUS: Geographical features (ocean, mountain, river, desert, island, waterfall, volcano, etc.). GRAMMAR RECYCLING: Use with comparatives ('The Amazon is longer than the Nile'), superlatives ('Mount Everest is the highest mountain'), THERE IS/THERE ARE ('There is a lake', 'There are mountains'), present simple for facts ('Canada has many lakes'), and CAN for possibilities ('You can visit the coast'). No new grammar - applying what you know to new vocabulary!",
  
  sets: [
    // SET 1: Water Features
    {
      id: "m4-u17-set1",
      title: "Set 1: Water Features",
      questions: [
        {
          type: "match",
          q: "Match water features to Spanish:",
          pairs: [
            { left: "ocean", right: "océano" },
            { left: "river", right: "río" },
            { left: "lake", right: "lago" }
          ],
          a: "completed",
          tip: "Water vocabulary! Essential for describing geography and nature!"
        },
        {
          type: "mc",
          q: "'A very large body of salt water - bigger than a sea' = ?",
          options: ["ocean", "lake", "pond"],
          a: "ocean",
          tip: "OCEAN! Huge! 🌊 Pacific, Atlantic, Indian = oceans. Salt water, covers most of Earth!"
        },
        {
          type: "input",
          q: "Complete: 'The Amazon is the longest ___ in South America.' (río)",
          a: "river"
        },
        {
          type: "mc",
          q: "'Water falling from a high place - like Niagara Falls' = ?",
          options: ["waterfall", "river", "stream"],
          a: "waterfall",
          tip: "WATERFALL! Water falls down! Niagara Falls = famous Canadian waterfall!"
        },
        {
          type: "order",
          q: "Ordena: 'Ontario tiene miles de lagos'",
          words: ["Ontario", "has", "thousands", "of", "lakes."],
          a: "Ontario has thousands of lakes."
        },
        {
          type: "mc",
          q: "'A piece of land completely surrounded by water' = ?",
          options: ["island", "coast", "beach"],
          a: "island",
          successtip: "✓ ISLAND! Surrounded by water! 🏝️ Vancouver Island, Prince Edward Island = Canadian islands!"
        },
        {
          type: "input",
          q: "Complete: 'The ___ is where the land meets the ocean.' (costa)",
          a: "coast"
        },
        {
          type: "mc",
          q: "'A small body of fresh water, smaller than a lake' = ?",
          options: ["pond", "ocean", "sea"],
          a: "pond",
          tip: "POND! Small water body! Usually fresh water, often in parks or countryside!"
        },
        {
          type: "order",
          q: "Ordena: 'Me encanta caminar por la playa'",
          words: ["I", "love", "walking", "on", "the", "beach."],
          a: "I love walking on the beach."
        },
        {
          type: "mc",
          q: "'A small, narrow river' = ?",
          options: ["stream", "ocean", "lake"],
          a: "stream",
          tip: "STREAM! Small river! Water flows but smaller than a river. Often in forests!"
        }
      ]
    },

    // SET 2: Land Features
    {
      id: "m4-u17-set2",
      title: "Set 2: Land Features",
      questions: [
        {
          type: "match",
          q: "Match land features to Spanish:",
          pairs: [
            { left: "mountain", right: "montaña" },
            { left: "valley", right: "valle" },
            { left: "cliff", right: "acantilado" }
          ],
          a: "completed",
          tip: "Land vocabulary! Describe the terrain and landscape!"
        },
        {
          type: "mc",
          q: "'A very high area of land - higher than 600 meters' = ?",
          options: ["mountain", "hill", "field"],
          a: "mountain",
          tip: "MOUNTAIN! Very high! ⛰️ Rocky Mountains, Mount Logan = Canadian mountains!"
        },
        {
          type: "input",
          q: "Complete: 'The Grand Canyon is the most famous ___ in North America.' (cañón)",
          a: "canyon"
        },
        {
          type: "mc",
          q: "'A deep narrow area of land between mountains' = ?",
          options: ["valley", "cave", "field"],
          a: "valley",
          successtip: "✓ VALLEY! Low area between mountains! Rivers often flow through valleys!"
        },
        {
          type: "order",
          q: "Ordena: 'Las montañas Rocosas son hermosas'",
          words: ["The", "Rocky", "Mountains", "are", "beautiful."],
          a: "The Rocky Mountains are beautiful."
        },
        {
          type: "mc",
          q: "'A high, steep area of rock near the ocean or river' = ?",
          options: ["cliff", "hill", "sand dune"],
          a: "cliff",
          tip: "CLIFF! Steep rock face! Dangerous - you can fall! Often near ocean or river!"
        },
        {
          type: "input",
          q: "Complete: 'A ___ is a natural underground space in rock.' (cueva)",
          a: "cave"
        },
        {
          type: "mc",
          q: "'An area of land outside cities and towns - usually with farms' = ?",
          options: ["countryside", "cave", "canyon"],
          a: "countryside",
          tip: "COUNTRYSIDE! Rural area! Not city! Fields, farms, nature. Peaceful and quiet!"
        },
        {
          type: "order",
          q: "Ordena: 'Los agricultores trabajan en los campos'",
          words: ["Farmers", "work", "in", "the", "fields."],
          a: "Farmers work in the fields."
        },
        {
          type: "mc",
          q: "'A hill of sand in a desert, formed by wind' = ?",
          options: ["sand dune", "mountain", "valley"],
          a: "sand dune",
          successtip: "✓ SAND DUNE! Desert hills! Made of sand! Wind creates them. Beautiful but hot!"
        }
      ]
    },

    // SET 3: Vegetation & Extreme Features
    {
      id: "m4-u17-set3",
      title: "Set 3: Vegetation & Extreme Features",
      questions: [
        {
          type: "match",
          q: "Match features to Spanish:",
          pairs: [
            { left: "desert", right: "desierto" },
            { left: "jungle", right: "jungla/selva" },
            { left: "volcano", right: "volcán" }
          ],
          a: "completed",
          tip: "Extreme and vegetation features! Describe diverse environments!"
        },
        {
          type: "mc",
          q: "'A very dry area with almost no rain, very hot, lots of sand' = ?",
          options: ["desert", "rainforest", "swamp"],
          a: "desert",
          tip: "DESERT! Very dry! 🏜️ Sahara = biggest hot desert. Almost no water or plants!"
        },
        {
          type: "input",
          q: "Complete: 'The Amazon ___ is the largest in the world.' (rainforest/jungle)",
          a: "rainforest / jungle"
        },
        {
          type: "mc",
          q: "'A tropical forest with very dense trees, hot and humid, lots of rain' = ?",
          options: ["rainforest / jungle", "desert", "woods"],
          a: "rainforest / jungle",
          successtip: "✓ RAINFOREST/JUNGLE! Dense trees! 🌳 Hot, humid, lots of rain. Amazon = famous rainforest!"
        },
        {
          type: "order",
          q: "Ordena: 'Los volcanes pueden ser peligrosos'",
          words: ["Volcanoes", "can", "be", "dangerous."],
          a: "Volcanoes can be dangerous."
        },
        {
          type: "mc",
          q: "'A mountain that can erupt with hot lava and ash' = ?",
          options: ["volcano", "glacier", "hill"],
          a: "volcano",
          tip: "VOLCANO! Erupts! 🌋 Hot lava comes out! Active volcanoes are dangerous but amazing!"
        },
        {
          type: "input",
          q: "Complete: 'A ___ is a huge mass of ice that moves slowly.' (glaciar)",
          a: "glacier"
        },
        {
          type: "mc",
          q: "'A very cold region near the North or South Pole' = ?",
          options: ["polar region", "desert", "jungle"],
          a: "polar region",
          tip: "POLAR REGION! Very cold! ❄️ Antarctica, Arctic = polar regions. Ice, snow, penguins!"
        },
        {
          type: "order",
          q: "Ordena: 'Hay un oasis en medio del desierto'",
          words: ["There", "is", "an", "oasis", "in", "the", "middle", "of", "the", "desert."],
          a: "There is an oasis in the middle of the desert."
        },
        {
          type: "mc",
          q: "'A small area in the woods, smaller than a forest' = ?",
          options: ["woods", "jungle", "swamp"],
          a: "woods",
          successtip: "✓ WOODS! Small forest! 🌲 Trees, nature, often near countryside. Good for hiking!"
        }
      ]
    },

    // SET 4: MINI-READING 1 - Canadian Geography
    {
      id: "m4-u17-set4",
      title: "Set 4: Reading - Canadian Geography",
      questions: [
        {
          type: "mc",
          q: "Lee el texto:\n\n**Canada's Amazing Geography**\n\nCanada has incredible geographical diversity! In the west, there are the Rocky Mountains - the highest mountains in Canada. British Columbia has a long coast on the Pacific Ocean with beautiful beaches and many islands. Vancouver Island is the biggest island on the west coast.\n\nThe prairie provinces (Alberta, Saskatchewan, Manitoba) have flat countryside with huge fields. Farmers grow wheat in these fields. There are no mountains here - just flat land!\n\nIn the north, there are polar regions with glaciers and icebergs. It's very cold! Nunavut is Canada's largest polar region.\n\nOntario has more lakes than any other province - over 250,000 lakes! The Great Lakes are the biggest. The Niagara River connects two of the Great Lakes and has the famous Niagara Falls waterfall.\n\nCanada also has rainforests! The Great Bear Rainforest in British Columbia is one of the largest temperate rainforests in the world.\n\nCanada's geography is the most diverse in North America!\n\nHow many types of geographical features are mentioned?",
          options: [
            "Many! (mountains, coast, ocean, islands, countryside, fields, polar regions, glaciers, icebergs, lakes, river, waterfall, rainforests)",
            "Only 3-4",
            "Just mountains and lakes"
          ],
          a: "Many! (mountains, coast, ocean, islands, countryside, fields, polar regions, glaciers, icebergs, lakes, river, waterfall, rainforests)",
          tip: "DIVERSE GEOGRAPHY! Canada has almost every type of geographical feature!"
        },
        {
          type: "mc",
          q: "The Rocky Mountains are in:",
          options: [
            "Western Canada (British Columbia, Alberta)",
            "Ontario",
            "The prairies only"
          ],
          a: "Western Canada (British Columbia, Alberta)"
        },
        {
          type: "mc",
          q: "Which province has the most lakes?",
          options: [
            "Ontario (250,000+ lakes)",
            "British Columbia",
            "Nunavut"
          ],
          a: "Ontario (250,000+ lakes)",
          successtip: "✓ ONTARIO! Lake province! More lakes than any other province. Great Lakes are here too!"
        },
        {
          type: "mc",
          q: "The prairie provinces have:",
          options: [
            "Flat countryside with huge fields (no mountains)",
            "Many mountains",
            "Many oceans"
          ],
          a: "Flat countryside with huge fields (no mountains)"
        },
        {
          type: "mc",
          q: "Canada has rainforests in:",
          options: [
            "British Columbia (Great Bear Rainforest)",
            "Ontario",
            "The prairies"
          ],
          a: "British Columbia (Great Bear Rainforest)",
          successtip: "✓ BC RAINFOREST! Surprising! Not all rainforests are tropical! Temperate rainforests exist in BC!"
        }
      ]
    },

    // SET 5: Grammar Connection (Comparatives/Superlatives)
    {
      id: "m4-u17-set5",
      title: "Set 5: Describing with Comparatives & Superlatives",
      questions: [
        {
          type: "match",
          q: "Match geographical comparison:",
          pairs: [
            { left: "Mount Everest is THE highest mountain", right: "Superlative" },
            { left: "The Amazon is longer THAN the Nile", right: "Comparative" },
            { left: "Canada HAS many lakes", right: "Present simple fact" }
          ],
          a: "completed",
          tip: "Geography uses comparatives and superlatives A LOT! World records and facts!"
        },
        {
          type: "mc",
          q: "'Mount Everest is ___ mountain in the world.' (el más alto)",
          options: ["the highest", "higher than", "high"],
          a: "the highest",
          tip: "THE HIGHEST! Superlative! Everest = world record! 8,849 meters high!"
        },
        {
          type: "input",
          q: "Complete: 'The Pacific Ocean is ___ than the Atlantic Ocean.' (bigger)",
          a: "bigger"
        },
        {
          type: "order",
          q: "Ordena: 'El Sahara es el desierto más grande del mundo'",
          words: ["The", "Sahara", "is", "the", "biggest", "desert", "in", "the", "world."],
          a: "The Sahara is the biggest desert in the world."
        },
        {
          type: "mc",
          q: "'The Amazon rainforest is ___ rainforest on Earth.'",
          options: ["the largest", "larger than", "large"],
          a: "the largest",
          successtip: "✓ THE LARGEST! Superlative for world record! Amazon = biggest rainforest, most biodiversity!"
        },
        {
          type: "input",
          q: "Complete: 'Mountains are ___ than hills.' (higher)",
          a: "higher"
        },
        {
          type: "mc",
          q: "'Antarctica is ___ place on Earth.' (más frío)",
          options: ["the coldest", "colder than", "cold"],
          a: "the coldest",
          tip: "THE COLDEST! Extreme superlative! Antarctica can reach -89°C! Coldest ever recorded!"
        },
        {
          type: "order",
          q: "Ordena: 'El Nilo es más largo que el Mississippi'",
          words: ["The", "Nile", "is", "longer", "than", "the", "Mississippi."],
          a: "The Nile is longer than the Mississippi."
        },
        {
          type: "input",
          q: "Complete: 'Canada has ___ longest coastline in the world.' (the)",
          a: "the"
        },
        {
          type: "mc",
          q: "Combining forms: 'The Amazon is longer than the Nile, but many people think the Nile is ___ longest river.' (controversial fact)",
          options: ["the (both claims exist - depends on measurement)", "than", "more"],
          a: "the (both claims exist - depends on measurement)",
          successtip: "✓ GEOGRAPHICAL DEBATE! Amazon vs Nile = longest river debate! Depends on how you measure sources!"
        }
      ]
    },

    // SET 6: MINI-READING 2 - Travel Blog
    {
      id: "m4-u17-set6",
      title: "Set 6: Reading - Travel Blog",
      questions: [
        {
          type: "mc",
          q: "Lee el texto:\n\n**My Trip to Costa Rica - Day 3**\n\n**Posted by Sarah_Travels**\n\nToday we're visiting the Arenal Volcano! It's one of the most active volcanoes in Costa Rica. The view is amazing! The volcano is surrounded by jungle - so green and beautiful. I love the rainforest here.\n\nTomorrow we're going to La Fortuna Waterfall. It's a huge waterfall in the jungle. I can't wait! The water falls 70 meters - that's very high!\n\nAfter that, we want to go to the coast and relax on the beach. Costa Rica has beautiful beaches on both the Pacific Ocean and the Caribbean Sea. We're planning to visit Manuel Antonio - it has incredible beaches and you can see monkeys in the trees!\n\nCosta Rica also has many islands, but we can't visit them - we don't have enough time. Maybe next trip!\n\nI'm comparing Costa Rica to Canada (my home country). The mountains here are smaller than the Canadian Rockies, but they're more tropical and green. There are also caves near our hotel. We're thinking of exploring them tomorrow before we leave.\n\nThis country has THE most diverse geography I've ever seen in such a small place!\n\nWhat geographical features is Sarah visiting?",
          options: [
            "Many! (volcano, jungle/rainforest, waterfall, coast, beach, ocean, sea, islands, mountains, caves)",
            "Only beach and ocean",
            "Just volcano"
          ],
          a: "Many! (volcano, jungle/rainforest, waterfall, coast, beach, ocean, sea, islands, mountains, caves)",
          tip: "COSTA RICA = DIVERSE! Small country, many geographical features! Perfect example!"
        },
        {
          type: "mc",
          q: "Arenal is a:",
          options: [
            "Volcano (active volcano)",
            "Mountain",
            "Beach"
          ],
          a: "Volcano (active volcano)"
        },
        {
          type: "mc",
          q: "What grammar does Sarah use for future plans?",
          options: [
            "Present continuous (we're visiting, we're going, we're planning)",
            "Past simple",
            "Future perfect"
          ],
          a: "Present continuous (we're visiting, we're going, we're planning)",
          successtip: "✓ PRESENT CONTINUOUS! For plans! 'We're visiting' = future plan. Review U14!"
        },
        {
          type: "mc",
          q: "La Fortuna Waterfall is:",
          options: [
            "70 meters high - very high waterfall",
            "Small waterfall",
            "Not mentioned"
          ],
          a: "70 meters high - very high waterfall"
        },
        {
          type: "mc",
          q: "Sarah uses which comparison form?",
          options: [
            "Both! Comparative ('smaller than') and superlative ('the most diverse')",
            "Only comparative",
            "Only superlative"
          ],
          a: "Both! Comparative ('smaller than') and superlative ('the most diverse')",
          successtip: "✓ BOTH FORMS! Natural writing! Comparing Costa Rica mountains to Canada (comparative), then saying most diverse (superlative)!"
        }
      ]
    },

    // SET 7: There is/There are with Geography
    {
      id: "m4-u17-set7",
      title: "Set 7: Describing Places - There is/There are",
      questions: [
        {
          type: "match",
          q: "Match structure to use:",
          pairs: [
            { left: "There is (singular)", right: "There is a lake" },
            { left: "There are (plural)", right: "There are mountains" },
            { left: "There is no/There are no", right: "Negative form" }
          ],
          a: "completed",
          tip: "THERE IS/ARE = describing what exists in a place! Essential for geography!"
        },
        {
          type: "mc",
          q: "'___ a beautiful waterfall near the hotel.'",
          options: ["There is", "There are", "It is"],
          a: "There is",
          tip: "THERE IS! Singular! One waterfall = there is. Use for describing existence!"
        },
        {
          type: "input",
          q: "Complete: 'There ___ many islands in the Pacific Ocean.' (are)",
          a: "are"
        },
        {
          type: "order",
          q: "Ordena: 'Hay un volcán activo en Costa Rica'",
          words: ["There", "is", "an", "active", "volcano", "in", "Costa", "Rica."],
          a: "There is an active volcano in Costa Rica."
        },
        {
          type: "mc",
          q: "'___ mountains in the prairies.'",
          options: ["There are no / There aren't", "There is no", "There are"],
          a: "There are no / There aren't",
          successtip: "✓ THERE ARE NO! Negative plural! Prairies = flat, no mountains! Grammar + geography fact!"
        },
        {
          type: "input",
          q: "Complete: 'There ___ a lake near my house.' (is)",
          a: "is"
        },
        {
          type: "mc",
          q: "'___ glaciers in Antarctica.'",
          options: ["There are", "There is", "It is"],
          a: "There are",
          tip: "THERE ARE! Plural! Many glaciers = there are. Antarctica has many glaciers!"
        },
        {
          type: "order",
          q: "Ordena: 'No hay selvas en Canadá - hay bosques'",
          words: ["There", "are", "no", "jungles", "in", "Canada", "-", "there", "are", "woods."],
          a: "There are no jungles in Canada - there are woods."
        },
        {
          type: "input",
          q: "Complete: '___ is a desert called the Sahara in Africa.' (There)",
          a: "There"
        },
        {
          type: "mc",
          q: "Question form: '___ any caves near here?'",
          options: ["Are there", "Is there", "There are"],
          a: "Are there",
          successtip: "✓ ARE THERE! Question form plural! 'Are there any...?' = asking if multiple things exist!"
        }
      ]
    },

    // SET 8: MINI-READING 3 - World Wonders
    {
      id: "m4-u17-set8",
      title: "Set 8: Reading - World's Geographical Wonders",
      questions: [
        {
          type: "mc",
          q: "Lee el texto:\n\n**The Most Amazing Geographical Features in the World**\n\n**The Grand Canyon (USA)** - This is the most famous canyon in the world. It's 446 km long and 1.6 km deep! The Colorado River created this canyon over millions of years. It's deeper than any other canyon in North America. You can see beautiful red rocks and cliffs. There are also caves in the canyon walls.\n\n**The Amazon River & Rainforest (South America)** - The Amazon is one of the longest rivers in the world (some say it's longer than the Nile!). The Amazon Rainforest around it is THE biggest rainforest on Earth. It has more species of plants and animals than any other place. The jungle is so dense, it's hard to walk through!\n\n**The Sahara Desert (Africa)** - The largest hot desert in the world. It's bigger than the entire United States! The Sahara has huge sand dunes - some are 180 meters high! There are very few oases in this desert. It's one of the hottest and driest places on Earth.\n\n**Antarctica (South Pole)** - The coldest place on Earth. This is a polar region completely covered in ice. Antarctica has the largest glaciers in the world. There are also huge icebergs floating in the ocean around Antarctica. No trees grow here - it's too cold!\n\n**Mount Everest (Nepal/Tibet)** - THE highest mountain in the world at 8,849 meters. It's higher than any other mountain on Earth. Many people try to climb it, but it's very dangerous. The top is always covered in snow and ice.\n\nHow many geographical features are described?",
          options: [
            "Five major places with multiple features (canyon, river, cliffs, caves, rainforest, jungle, desert, sand dunes, oases, polar region, glaciers, icebergs, mountain)",
            "Only three",
            "Just mountains"
          ],
          a: "Five major places with multiple features (canyon, river, cliffs, caves, rainforest, jungle, desert, sand dunes, oases, polar region, glaciers, icebergs, mountain)"
        },
        {
          type: "mc",
          q: "The Grand Canyon was created by:",
          options: [
            "The Colorado River (over millions of years)",
            "Humans",
            "A volcano"
          ],
          a: "The Colorado River (over millions of years)",
          tip: "RIVER POWER! Rivers can create canyons over time! Water erosion = powerful force!"
        },
        {
          type: "mc",
          q: "Which uses the most superlatives?",
          options: [
            "This text! (most famous, biggest, largest, longest, highest, coldest, hottest, driest)",
            "Uses no superlatives",
            "Only one superlative"
          ],
          a: "This text! (most famous, biggest, largest, longest, highest, coldest, hottest, driest)",
          successtip: "✓ WORLD RECORDS! Geography vocabulary + superlatives = natural combination! Describing extremes!"
        },
        {
          type: "mc",
          q: "The Sahara Desert has:",
          options: [
            "Huge sand dunes (some 180m high) and very few oases",
            "Many rivers and lakes",
            "Lots of trees"
          ],
          a: "Huge sand dunes (some 180m high) and very few oases"
        },
        {
          type: "mc",
          q: "Antarctica is described as:",
          options: [
            "The coldest polar region with glaciers and icebergs, no trees",
            "A hot desert",
            "A rainforest"
          ],
          a: "The coldest polar region with glaciers and icebergs, no trees",
          successtip: "✓ EXTREME COLD! Polar opposite of desert! Too cold for trees. Only ice, glaciers, icebergs!"
        }
      ]
    },

    // SET 9: Personal Geography
    {
      id: "m4-u17-set9",
      title: "Set 9: Describing Your Geography",
      questions: [
        {
          type: "match",
          q: "Match personal geography phrases:",
          pairs: [
            { left: "In my country, there is/are...", right: "Describing your country" },
            { left: "Near my city, you can see...", right: "Describing nearby features" },
            { left: "I want to visit...", right: "Personal travel goal" }
          ],
          a: "completed",
          tip: "Personal application! Talk about YOUR country's geography!"
        },
        {
          type: "mc",
          q: "Describing your country: 'In my country, there ___'",
          options: [
            "is / are [geographical features] (share YOUR country's geography!)",
            "was",
            "will be"
          ],
          a: "is / are [geographical features] (share YOUR country's geography!)",
          tip: "EXPRESS YOURSELF! 'In Mexico, there are mountains and beaches' - what's in YOUR country?"
        },
        {
          type: "input",
          q: "Complete: 'Near my city, there ___ a ___.' (is/are + feature)",
          a: "(Your personal answer - is/are + geographical feature)"
        },
        {
          type: "order",
          q: "Ordena: 'Mi país tiene hermosas montañas'",
          words: ["My", "country", "has", "beautiful", "mountains."],
          a: "My country has beautiful mountains."
        },
        {
          type: "mc",
          q: "Expressing preference: 'I prefer ___ because...'",
          options: [
            "mountains / beaches / countryside / cities (YOUR preference!)",
            "Yes",
            "No"
          ],
          a: "mountains / beaches / countryside / cities (YOUR preference!)",
          successtip: "✓ PERSONAL OPINION! Geography + preferences! 'I prefer mountains because they're beautiful and peaceful!'"
        },
        {
          type: "input",
          q: "Complete: 'I want to visit ___ because ___.' (place + reason)",
          a: "(Your personal answer - any place + reason)"
        },
        {
          type: "mc",
          q: "Comparing geographies: 'My country is ___ than Canada.'",
          options: [
            "hotter / colder / smaller / bigger / drier (YOUR comparison!)",
            "Yes",
            "Always the same"
          ],
          a: "hotter / colder / smaller / bigger / drier (YOUR comparison!)",
          tip: "COMPARE! Use comparatives! 'Mexico is hotter than Canada' - how is YOUR country different?"
        },
        {
          type: "order",
          q: "Ordena: 'El lugar más hermoso de mi país es...'",
          words: ["The", "most", "beautiful", "place", "in", "my", "country", "is..."],
          a: "The most beautiful place in my country is..."
        },
        {
          type: "input",
          q: "Complete: 'In my region, the weather is ___ in summer.' (describe)",
          a: "(Your personal answer - hot/warm/cool/rainy/dry/etc.)"
        },
        {
          type: "mc",
          q: "Full description: 'In my country, there are ___, the climate is ___, and I think it's ___.'",
          options: [
            "YOUR complete description using geographical vocabulary!",
            "I don't know",
            "Yes"
          ],
          a: "YOUR complete description using geographical vocabulary!",
          successtip: "✓ COMPLETE DESCRIPTION! Geography + climate + opinion! Example: 'In Mexico, there are beautiful beaches and mountains, the climate is hot and sunny, and I think it's amazing!'"
        }
      ]
    },

    // SET 10: MINI-READING 4 - Vacation Planning
    {
      id: "m4-u17-set10",
      title: "Set 10: Reading - Vacation Planning Conversation",
      questions: [
        {
          type: "mc",
          q: "Lee la conversación:\n\n**Planning Our Dream Vacation**\n\n**Emma:** I'm so excited! We're finally planning our vacation! I can't decide - beach or mountains?\n\n**Carlos:** What do you prefer? I think you love the ocean!\n\n**Emma:** I do! I love swimming in the ocean and relaxing on the beach. But I also like hiking in the mountains.\n\n**Carlos:** What about a tropical island? There are beautiful islands in the Caribbean. They have beaches AND mountains!\n\n**Emma:** Perfect! Are there waterfalls there too? I love waterfalls!\n\n**Carlos:** Yes! Many Caribbean islands have rainforests with waterfalls. Jamaica has beautiful waterfalls in the jungle.\n\n**Emma:** Amazing! What about volcanoes? I want to see a volcano!\n\n**Carlos:** Hmm... There are volcanoes in Hawaii! Hawaii has active volcanoes, beautiful beaches, mountains, waterfalls, AND tropical rainforests. It has everything!\n\n**Emma:** That sounds perfect! But it's probably very expensive. We can't afford Hawaii right now.\n\n**Carlos:** You're right. What about Costa Rica? It's cheaper than Hawaii and has the same features - volcanoes, rainforests, waterfalls, beaches, and mountains!\n\n**Emma:** I love it! I prefer beaches, but I also want to see diverse geography. Costa Rica is perfect!\n\n**Carlos:** Great! There are also caves there we can explore. And the coast is beautiful.\n\n**Emma:** Perfect! Let's book it! Costa Rica has THE best combination of geographical features!\n\nHow many geographical features do they discuss?",
          options: [
            "Many! (beach, mountains, ocean, island, waterfalls, rainforests, jungle, volcanoes, caves, coast)",
            "Only 2-3",
            "Just beaches"
          ],
          a: "Many! (beach, mountains, ocean, island, waterfalls, rainforests, jungle, volcanoes, caves, coast)"
        },
        {
          type: "mc",
          q: "What grammar do they use for future plans?",
          options: [
            "Present continuous ('we're planning', 'Let's book') and WANT TO ('I want to see')",
            "Past simple",
            "Future perfect"
          ],
          a: "Present continuous ('we're planning', 'Let's book') and WANT TO ('I want to see')",
          tip: "MIXED GRAMMAR! Present continuous for arrangements + want to for desires. Natural conversation!"
        },
        {
          type: "mc",
          q: "Why don't they choose Hawaii?",
          options: [
            "Too expensive - they can't afford it",
            "No beaches",
            "No volcanoes"
          ],
          a: "Too expensive - they can't afford it",
          successtip: "✓ BUDGET REALITY! CAN'T AFFORD! Realistic conversation. Cost matters in travel planning!"
        },
        {
          type: "mc",
          q: "They choose Costa Rica because:",
          options: [
            "Cheaper than Hawaii but has the same geographical features (volcanoes, rainforests, beaches, mountains, waterfalls, caves)",
            "Only has beaches",
            "It's expensive"
          ],
          a: "Cheaper than Hawaii but has the same geographical features (volcanoes, rainforests, beaches, mountains, waterfalls, caves)"
        },
        {
          type: "mc",
          q: "What comparison form appears at the end?",
          options: [
            "Superlative! 'THE best combination'",
            "Comparative only",
            "No comparisons"
          ],
          a: "Superlative! 'THE best combination'",
          successtip: "✓ SUPERLATIVE CONCLUSION! 'THE best' = final decision! Costa Rica wins! Geography + superlative = perfect combo!"
        }
      ]
    }
  ],
  // ===================================================================
// READING SET: Planet Earth - Nature Documentary
// ===================================================================
readingSet: {
  id: "m4-u17-reading",
  title: "Set 11: Full Reading - Nature Documentary Script",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: Introduction & Water (preguntas 1, 2 y 3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Introduction & Part 1: Water**

**NARRATOR:** Welcome to Planet Earth—our home. Today, we're exploring the most amazing geographical features on our planet.

Let's start with water. Oceans cover 70% of Earth's surface. The Pacific Ocean is **the largest** ocean—it's bigger than all the continents combined! Rivers like the Amazon and the Nile are **the longest** rivers in the world. 

Waterfalls are where rivers fall from high places. Angel Falls in Venezuela is **the highest** waterfall in the world—water falls 979 meters! That's higher than three Eiffel Towers! Lakes are bodies of fresh water. The Great Lakes in North America are **the largest** group of freshwater lakes in the world.`
    },
    {
      type: "mc",
      q: "¿Cuántas secciones principales tiene el documental según el texto?",
      options: [
        "Cuatro partes principales (Water, Land, Extreme Environments, Vegetation) más Intro y Conclusión",
        "Solo dos secciones",
        "Es una sola sección continua"
      ],
      a: "Four main parts (Water, Land, Extreme Environments, Vegetation) plus Introduction and Conclusion",
      successtip: "✓ STRUCTURE!\nHas identificado correctamente la organización del guion. 📑"
    },
    {
      type: "mc",
      q: "El Océano Pacífico es descrito como:",
      options: [
        "El océano más grande - más grande que todos los continentes juntos",
        "El océano más pequeño",
        "Un lago gigante"
      ],
      a: "The largest ocean - bigger than all continents combined",
      successtip: "✓ OCEAN GIANT!\nEs la masa de agua más extensa del planeta. 🌊"
    },
    {
      type: "mc",
      q: "¿Cuál es la característica principal del Salto Ángel (Angel Falls) en Venezuela?",
      options: [
        "Es la cascada más alta del mundo (979 metros)",
        "Es el río más largo",
        "Es la montaña más difícil de escalar"
      ],
      a: "The highest waterfall in the world (979 meters)",
      tip: "¡Dato curioso! 979 metros es casi un kilómetro de caída libre. 💧",
      successtip: "✓ WATERFALL RECORD!\n¡Impresionante! Es más alta que tres Torres Eiffel. 🇻🇪"
    },

    

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Landforms - Mountains, Valleys & Caves (preguntas 4 y 5)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Part 2: Land**

Mountains are the highest landforms. Mount Everest is **THE highest** mountain at 8,849 meters. Valleys are low areas between mountains. Canyons are deep, narrow valleys with steep sides. 

The Grand Canyon is **the most famous** canyon—it's 446 km long and over 1 km deep! Caves are natural underground spaces. Mammoth Cave in Kentucky is **the longest** cave system in the world—over 650 km of caves!`
    },
    {
      type: "mc",
      q: "¿Cuál es la altura exacta del Monte Everest según el guion?",
      options: [
        "8,849 metros - La montaña más alta de la Tierra",
        "1,000 metros",
        "5,500 metros"
      ],
      a: "8,849 meters - THE highest mountain on Earth",
      successtip: "✓ MOUNTAIN PEAK!\nEverest es el punto más alto sobre el nivel del mar. 🏔️"
    },
    {
      type: "mc",
      q: "¿Cómo se describe al Gran Cañón (Grand Canyon)?",
      options: [
        "El cañón más famoso - 446 km de largo y más de 1 km de profundidad",
        "Un pequeño valle con flores",
        "Una montaña rusa natural"
      ],
      a: "The most famous canyon - 446 km long, over 1 km deep",
      successtip: "✓ CANYON MASTER!\nUna de las maravillas naturales más impresionantes del mundo. 🏜️"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Extreme Environments (preguntas 6 y 7)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Part 3: Extreme Environments**

Deserts are **the driest** places on Earth. The Sahara Desert in Africa is **the largest** hot desert. It has huge sand dunes created by wind. Polar regions are **the coldest** places. 

Antarctica is **THE coldest** place on the planet—temperatures can drop to -89°C! It is covered in ice. Glaciers are huge masses of ice, and icebergs are pieces that break off and float in the ocean.`
    },
    {
      type: "mc",
      q: "¿Qué característica destaca del Desierto del Sahara?",
      options: [
        "Tiene dunas de arena gigantes (hasta 180m) y es el desierto cálido más grande",
        "Tiene mucha agua y árboles frutales",
        "Está cubierto de nieve y hielo"
      ],
      a: "Huge sand dunes (up to 180m high) - largest hot desert",
      successtip: "✓ DESERT HEAT!\nEl Sahara es el desierto cálido más extenso. 🏜️☀️"
    },
    {
      type: "mc",
      q: "¿Cuál es la temperatura más baja registrada en la Antártida según el texto?",
      options: [
        "-89°C - El lugar más frío de la Tierra",
        "0°C",
        "30°C"
      ],
      a: "-89°C - THE coldest place on Earth",
      successtip: "✓ POLAR EXTREME!\nUn frío inimaginable donde solo hay hielo y glaciares. ❄️🇦🇳"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Vegetation & Conclusion (preguntas 8, 9 y 10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Part 4: Vegetation & Conclusion**

Rainforests are tropical forests with dense trees. They're hot and humid. The Amazon Rainforest has **more species** of plants and animals than anywhere else. 

**CONCLUSION:** Earth has incredible geographical diversity! From **the highest** mountains to **the deepest** canyons, our planet has everything. We must protect these amazing places for future generations.`
    },

    

    {
      type: "mc",
      q: "¿Cómo se describe a la Selva Amazónica (Amazon Rainforest)?",
      options: [
        "La selva más grande de la Tierra con la mayor biodiversidad de especies",
        "Un pequeño bosque en el campo",
        "Un desierto sin vida"
      ],
      a: "The biggest rainforest on Earth with the most species",
      successtip: "✓ GREEN LUNG!\nEs el ecosistema con más biodiversidad del mundo. 🌳🦜"
    },
    {
      type: "mc",
      q: "¿Por qué el documental utiliza tantos superlativos (largest, highest, coldest)?",
      options: [
        "Porque habla de récords mundiales y características extremas",
        "Porque solo conoce esos adjetivos",
        "Es un error del narrador"
      ],
      a: "MANY! (largest, biggest, highest, longest, most famous, coldest, deepest, hottest, most dangerous, most species)",
      tip: "¡SUPERLATIVOS!\nSon esenciales para documentales que muestran 'lo más' de nuestro planeta. 🌍",
      successtip: "✓ GRAMMAR GENIUS!\nHas notado el uso natural de los superlativos para resaltar la diversidad. 🎯"
    },
    {
      type: "mc",
      q: "¿Cuál es el mensaje principal de la conclusión del documental?",
      options: [
        "La Tierra tiene una diversidad increíble y debemos proteger estos lugares",
        "Solo existe un tipo de geografía en el mundo",
        "La geografía no es importante para el futuro"
      ],
      a: "Earth has incredible geographical diversity - from highest to deepest, hottest to coldest. We must protect these places.",
      successtip: "✓ READING COMPLETE! 🎉\n¡Felicidades! Has terminado este viaje geográfico. Has dominado el vocabulario de la naturaleza y los superlativos. 🏆"
    }
  ]
},
  // SET 12: Review
  reviewSet: {
    id: "m4-u17-review",
    title: "Set 12: Unit Review",
    category: "review",
    questions: [
      {
        type: "match",
        q: "Match feature to category:",
        pairs: [
          { left: "Water features", right: "ocean, river, lake, waterfall" },
          { left: "Land features", right: "mountain, valley, cave, cliff" },
          { left: "Extreme features", right: "desert, volcano, glacier, polar region" }
        ],
        a: "completed"
      },
      {
        type: "mc",
        q: "Largest ocean:",
        options: ["Pacific Ocean", "Atlantic Ocean", "A lake"],
        a: "Pacific Ocean"
      },
      {
        type: "mc",
        q: "Highest mountain:",
        options: ["Mount Everest (8,849m)", "Rocky Mountains", "A hill"],
        a: "Mount Everest (8,849m)"
      },
      {
        type: "input",
        q: "Complete: 'A ___ is a mountain that can erupt with lava.' (volcano)",
        a: "volcano"
      },
      {
        type: "input",
        q: "Complete: 'The ___ is the largest hot desert in the world.' (Sahara)",
        a: "Sahara"
      },
      {
        type: "order",
        q: "Ordena: 'Canadá tiene la costa más larga del mundo'",
        words: ["Canada", "has", "the", "longest", "coastline", "in", "the", "world."],
        a: "Canada has the longest coastline in the world."
      },
      {
        type: "mc",
        q: "Grammar used with geography:",
        options: [
          "All! Comparatives, superlatives, there is/are, present simple, CAN",
          "Only past simple",
          "No grammar"
        ],
        a: "All! Comparatives, superlatives, there is/are, present simple, CAN"
      },
      {
        type: "mc",
        q: "'There ___ many lakes in Ontario.'",
        options: ["are", "is", "was"],
        a: "are"
      },
      {
        type: "mc",
        q: "Coldest place on Earth:",
        options: ["Antarctica (polar region)", "Sahara Desert", "Amazon Rainforest"],
        a: "Antarctica (polar region)"
      },
      {
        type: "mc",
        q: "FINAL MASTERY - Complete description:\n\n'Earth is amazing! The ___ Ocean is the largest ocean. Mount Everest is the ___ mountain at 8,849 meters. The Amazon is one of the ___ rivers in the world, and the Amazon ___ is the biggest rainforest on Earth. The Sahara ___ is the largest hot desert with huge sand ___. ___ is the coldest place - it's a polar ___ covered in glaciers and ___. There ___ waterfalls, caves, volcanoes, islands, and beautiful ___ all over the world. Canada ___ the longest coastline. We must protect these incredible geographical ___!'\n\nCorrect words:",
        options: [
          "Pacific / highest / longest / Rainforest / Desert / dunes / Antarctica / region / icebergs / are / beaches / has / features",
          "Atlantic / biggest / shortest / jungle / ocean / mountains / Canada / place / glaciers / is / cliffs / is / things",
          "Indian / tallest / smallest / woods / sea / hills / USA / area / rivers / were / coasts / have / places"
        ],
        a: "Pacific / highest / longest / Rainforest / Desert / dunes / Antarctica / region / icebergs / are / beaches / has / features",
        successtip: "✓ UNIT 17 COMPLETE! Geographical vocabulary mastered! 🌍 You learned: Water features (ocean, river, lake, waterfall, coast, island), Land features (mountain, valley, canyon, cave, cliff, countryside, field), Extreme environments (desert, volcano, glacier, polar region, iceberg, sand dune, oasis), Vegetation (rainforest, jungle, woods, swamp)! PLUS: Applied grammar naturally (comparatives, superlatives, there is/are, present simple, CAN), read 6 texts (Canadian geography, travel blog, world wonders, vacation planning, documentary), connected personally (described YOUR country's geography), used vocabulary in context! From basic vocabulary to complex geographical descriptions! You can now describe ANY place on Earth! 🏔️🌊🏜️🌴🗺️"
      }
    ]
  }
};
